import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';

export default function ServicesPage() {
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    // Page title
    document.title = 'Insulation Services Yuma AZ | Residential & Commercial';

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Complete spray foam insulation services in Yuma, AZ. Residential & commercial solutions. Closed cell, open cell, attic, wall insulation. Call (928) 427-8140.'
      );
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/services');

    // Geo-location tags
    const metaTags = [
      { name: 'geo.region', content: 'US-AZ' },
      { name: 'geo.placename', content: 'Yuma' },
      { name: 'geo.position', content: '32.6927;-114.6277' },
      { name: 'ICBM', content: '32.6927, -114.6277' },
    ];
    metaTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Insulation Services Yuma AZ | Residential & Commercial' },
      {
        property: 'og:description',
        content: 'Complete spray foam insulation services in Yuma, AZ. Residential & commercial solutions.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.origin + '/services' },
      {
        property: 'og:image',
        content:
          'https://public.readdy.ai/ai/img_res/ad6052676e66a560924d1c8ec18fc0c7.jpg'
      }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Insulation Services Yuma AZ | Residential & Commercial' },
      {
        name: 'twitter:description',
        content: 'Complete spray foam insulation services. Residential & commercial solutions in Yuma, AZ.'
      },
      {
        name: 'twitter:image',
        content:
          'https://readdy.ai/api/search-image?query=Comprehensive%20spray%20foam%20insulation%20services%20showcase%20displaying%20various%20residential%20and%20commercial%20applications%20with%20professional%20equipment%20and%20expert%20installation%20techniques%20highlighting%20quality%20workmanship%20and%20energy%20efficiency%20solutions&width=1200&height=630&seq=twitter-services-001&orientation=landscape'
      }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Last-modified tag
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Service schema
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Spray Foam Insulation Services',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Yuma Spray Foam Pros',
        telephone: '(928) 427-8140',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Yuma',
          addressRegion: 'AZ',
          addressCountry: 'US'
        }
      },
      areaServed: {
        '@type': 'City',
        name: 'Yuma',
        containedIn: {
          '@type': 'State',
          name: 'Arizona'
        }
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Insulation Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Residential Spray Foam Insulation'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Commercial Spray Foam Insulation'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Closed Cell Spray Foam'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Open Cell Spray Foam'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Attic Insulation'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Wall Insulation'
            }
          }
        ]
      }
    };

    let serviceScript = document.querySelector('script[data-schema="service-services"]');
    if (!serviceScript) {
      serviceScript = document.createElement('script');
      serviceScript.setAttribute('type', 'application/ld+json');
      serviceScript.setAttribute('data-schema', 'service-services');
      document.head.appendChild(serviceScript);
    }
    serviceScript.textContent = JSON.stringify(serviceSchema);

    // Breadcrumb schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin + '/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Services',
          item: window.location.origin + '/services'
        }
      ]
    };

    let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb-services"]');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-schema', 'breadcrumb-services');
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
  }, []);

  const commonServices = [
    {
      title: "Attic Insulation",
      icon: "ri-home-8-line",
      description: "Prevent heat gain and reduce cooling costs by up to 50% in scorching summers with complete attic application.",
      benefits: [
        "Reduces cooling costs by up to 50%",
        "Prevents heat transfer from roof",
        "Eliminates air leaks and drafts",
        "Improves indoor air quality"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/c9238f7676fdc1ad6e233e1ccaa36ed4.jpeg"
    },
    {
      title: "Crawl Space Insulation",
      icon: "ri-building-2-line",
      description: "Seal and protect crawl spaces to prevent moisture issues, improve air quality, and enhance overall home energy efficiency.",
      benefits: [
        "Prevents moisture and mold growth",
        "Improves indoor air quality",
        "Reduces energy bills year-round",
        "Protects foundation and structure"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/94bbd696513978aab0c6c2a15252b6d2.jpeg"
    },
    {
      title: "Garage Insulation",
      icon: "ri-car-line",
      description: "Transform your garage into a comfortable, temperature-controlled space perfect for workshops, storage, or additional living areas.",
      benefits: [
        "Creates comfortable workspace year-round",
        "Protects vehicles and stored items",
        "Reduces noise transmission",
        "Adds value to your property"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/92892655c4e9161c0cd0fedd52900b32.jpeg"
    },
    {
      title: "Wall Insulation",
      icon: "ri-layout-masonry-line",
      description: "New construction and retrofit wall work providing superior thermal protection and soundproofing for your home or business.",
      benefits: [
        "Superior thermal barrier protection",
        "Excellent soundproofing between rooms",
        "Prevents air infiltration completely",
        "Works for new builds and retrofits"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/32137e42f60d91c7bade9a61c1c75adc.jpeg"
    },
    {
      title: "Metal Building Insulation",
      icon: "ri-building-4-line",
      description: "Specialized solutions for metal buildings, preventing condensation and providing superior thermal performance.",
      benefits: [
        "Prevents condensation and rust",
        "Dramatically reduces energy costs",
        "Adds structural rigidity",
        "Creates comfortable work environment"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/42d4837eb8665fc5e913ec7d8c6bd364.jpeg"
    },
    {
      title: "New Construction",
      icon: "ri-hammer-line",
      description: "Partner with builders to provide superior work for new homes and commercial buildings from the ground up.",
      benefits: [
        "Optimal installation during construction",
        "Meets all building codes and standards",
        "Maximizes energy efficiency from day one",
        "Seamless integration with construction schedule"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/d094e68c658e1d8613db6be775725095.jpeg"
    },
    {
      title: "Retrofit Insulation",
      icon: "ri-tools-line",
      description: "Upgrade existing homes and buildings with modern solutions to dramatically improve comfort and efficiency.",
      benefits: [
        "Transforms existing building performance",
        "Minimal disruption to occupants",
        "Immediate energy savings",
        "Increases property value significantly"
      ],
      image: "https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/2c462353095da4fbb05b9c47dd24bebc.jpeg"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <Breadcrumb 
        items={[
          { label: 'Home', href: '/' },
          { label: 'Services' }
        ]}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-blue-900 font-semibold mb-3 tracking-wider">/OUR SERVICES</p>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Insulation Services Yuma AZ
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            From <a href="/residential-insulation-yuma" className="text-blue-900 hover:text-blue-800 font-semibold underline">residential homes</a> to large commercial facilities, we provide comprehensive solutions tailored to Arizona's unique desert climate. Our expert team delivers superior energy efficiency, comfort, and long-term value for every project in the area.
          </p>
        </div>
      </section>

      {/* Interactive Service Slider */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Popular Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore our most requested solutions. Swipe or click to learn more about each option.
            </p>
          </div>

          {/* Service Navigation Dots */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap">
            {commonServices.map((service, index) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeService === index
                    ? 'bg-blue-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className={`${service.icon} mr-2`}></i>
                {service.title}
              </button>
            ))}
          </div>

          {/* Active Service Display - Desktop */}
          <div className="hidden md:block bg-gray-50 rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-96 w-full">
                <img 
                  src={commonServices[activeService].image}
                  alt={`${commonServices[activeService].title} Yuma AZ`}
                  title={`Professional ${commonServices[activeService].title}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-10 flex flex-col justify-center">
                <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-6 w-fit">
                  <i className={`${commonServices[activeService].icon} mr-2`}></i>
                  {commonServices[activeService].title.toUpperCase()}
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {commonServices[activeService].title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                  {commonServices[activeService].description}
                </p>
                <ul className="space-y-3 mb-8">
                  {commonServices[activeService].benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start text-gray-600">
                      <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href="tel:928-427-8140" 
                  className="inline-block bg-blue-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (928) 427-8140
                </a>
              </div>
            </div>
          </div>

          {/* Active Service Display - Mobile (Swipeable) */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory">
              <div className="flex gap-4" style={{ width: 'max-content' }}>
                {commonServices.map((service, index) => (
                  <div 
                    key={index}
                    className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg snap-center"
                    style={{ width: '90vw', maxWidth: '400px', flexShrink: 0 }}
                  >
                    <div className="relative h-64 w-full">
                      <img 
                        src={service.image}
                        alt={`${service.title} Yuma AZ`}
                        title={`Professional ${service.title}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6">
                      <div className="inline-block bg-blue-100 text-blue-900 px-3 py-1 rounded-full text-xs font-semibold mb-4">
                        <i className={`${service.icon} mr-1`}></i>
                        {service.title.toUpperCase()}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                        {service.description}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start text-gray-600 text-sm">
                            <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      <a 
                        href="tel:928-427-8140" 
                        className="block bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer text-center text-sm"
                      >
                        <i className="ri-phone-line mr-2"></i>
                        Call (928) 427-8140
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center text-sm text-gray-500 mt-4">
              Swipe to see more services →
            </div>
          </div>

          {/* Navigation Arrows - Desktop Only */}
          <div className="hidden md:flex justify-center gap-4 mt-8">
            <button
              onClick={() => setActiveService(activeService === 0 ? commonServices.length - 1 : activeService - 1)}
              className="bg-gray-900 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button
              onClick={() => setActiveService(activeService === commonServices.length - 1 ? 0 : activeService + 1)}
              className="bg-gray-900 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Premium Products
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We use only the highest quality materials, specifically chosen for optimal performance in extreme desert climate.
            </p>
          </div>

          {/* Mobile: Horizontal Scroll */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory">
              <div className="flex gap-6" style={{ width: 'max-content' }}>
                <div className="bg-gray-50 p-8 rounded-2xl shadow-lg snap-center" style={{ width: '85vw', maxWidth: '400px', flexShrink: 0 }}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Closed-Cell Foam</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                    Our premium <a href="/closed-cell-spray-foam" className="text-blue-900 hover:text-blue-800 font-semibold underline">closed-cell option</a> offers the highest R-value per inch (R-6 to R-7), providing maximum efficiency. This dense material creates an impermeable moisture barrier and adds structural strength to your building.
                  </p>
                  <ul className="space-y-3 text-gray-600 mb-8 text-sm">
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Highest R-Value:</strong> R-6 to R-7 per inch for maximum efficiency</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Moisture Barrier:</strong> Prevents water infiltration and condensation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Structural Support:</strong> Adds rigidity and strength to walls and roofs</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Air Sealing:</strong> Complete airtight seal eliminates drafts</span>
                    </li>
                  </ul>
                  <p className="text-gray-600 mb-6 text-xs">
                    Perfect for demanding applications requiring superior protection in Yuma's extreme heat.
                  </p>
                  <a 
                    href="/closed-cell-spray-foam" 
                    className="inline-block bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Learn More <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>

                <div className="bg-gray-50 p-8 rounded-2xl shadow-lg snap-center" style={{ width: '85vw', maxWidth: '400px', flexShrink: 0 }}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Open-Cell Foam</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                    Our <a href="/open-cell-spray-foam" className="text-blue-900 hover:text-blue-800 font-semibold underline">open-cell option</a> (R-3.5 to R-4 per inch) provides excellent performance and sound dampening properties at a more economical price point. Perfect for interior walls and areas where moisture barriers aren't critical.
                  </p>
                  <ul className="space-y-3 text-gray-600 mb-8 text-sm">
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Cost-Effective:</strong> Excellent value for interior applications</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Sound Dampening:</strong> Superior noise reduction between rooms</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Flexibility:</strong> Expands more, filling irregular spaces easily</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-900 text-lg mr-2 mt-0.5"></i>
                      <span><strong>Breathable:</strong> Allows vapor permeability where needed</span>
                    </li>
                  </ul>
                  <p className="text-gray-600 mb-6 text-xs">
                    Ideal for soundproofing projects and interior applications throughout Arizona.
                  </p>
                  <a 
                    href="/open-cell-spray-foam" 
                    className="inline-block bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Learn More <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="text-center text-sm text-gray-500 mt-4">
              Swipe to see more →
            </div>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Closed-Cell Foam</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our premium <a href="/closed-cell-spray-foam" className="text-blue-900 hover:text-blue-800 font-semibold underline">closed-cell option</a> offers the highest R-value per inch (R-6 to R-7), providing maximum efficiency. This dense material creates an impermeable moisture barrier and adds structural strength to your building.
              </p>
              <ul className="space-y-3 text-gray-600 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Highest R-Value:</strong> R-6 to R-7 per inch for maximum efficiency</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Moisture Barrier:</strong> Prevents water infiltration and condensation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Structural Support:</strong> Adds rigidity and strength to walls and roofs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Air Sealing:</strong> Complete airtight seal eliminates drafts</span>
                </li>
              </ul>
              <p className="text-gray-600 mb-6 text-sm">
                Perfect for demanding applications requiring superior protection in Yuma's extreme heat.
              </p>
              <a 
                href="/closed-cell-spray-foam" 
                className="inline-block bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="bg-gray-50 p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Open-Cell Foam</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our <a href="/open-cell-spray-foam" className="text-blue-900 hover:text-blue-800 font-semibold underline">open-cell option</a> (R-3.5 to R-4 per inch) provides excellent performance and sound dampening properties at a more economical price point. Perfect for interior walls and areas where moisture barriers aren't critical.
              </p>
              <ul className="space-y-3 text-gray-600 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Cost-Effective:</strong> Excellent value for interior applications</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Sound Dampening:</strong> Superior noise reduction between rooms</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Flexibility:</strong> Expands more, filling irregular spaces easily</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span><strong>Breathable:</strong> Allows vapor permeability where needed</span>
                </li>
              </ul>
              <p className="text-gray-600 mb-6 text-sm">
                Ideal for soundproofing projects and interior applications throughout Arizona.
              </p>
              <a 
                href="/open-cell-spray-foam" 
                className="inline-block bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience the difference of working with the area's most trusted <a href="/about-yuma-insulation" className="text-blue-900 hover:text-blue-800 font-semibold underline">insulation professionals</a>.
            </p>
          </div>

          {/* Mobile: Horizontal Scroll */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory">
              <div className="flex gap-6" style={{ width: 'max-content' }}>
                <div className="bg-white p-8 rounded-xl shadow-md text-center snap-center" style={{ width: '280px', flexShrink: 0 }}>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-shield-check-line text-3xl text-blue-900"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Licensed & Insured</h3>
                  <p className="text-gray-600 text-sm">
                    Fully licensed, bonded, and insured for your protection and peace of mind on every project.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-md text-center snap-center" style={{ width: '280px', flexShrink: 0 }}>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-time-line text-3xl text-blue-900"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Installation</h3>
                  <p className="text-gray-600 text-sm">
                    Efficient installation process with minimal disruption to your home or business operations.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-md text-center snap-center" style={{ width: '280px', flexShrink: 0 }}>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-medal-line text-3xl text-blue-900"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Guarantee</h3>
                  <p className="text-gray-600 text-sm">
                    We stand behind our work with comprehensive warranties and exceptional customer service.
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center text-sm text-gray-500 mt-4">
              Swipe to see more →
            </div>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Licensed & Insured</h3>
              <p className="text-gray-600">
                Fully licensed, bonded, and insured for your protection and peace of mind on every project.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Installation</h3>
              <p className="text-gray-600">
                Efficient installation process with minimal disruption to your home or business operations.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-medal-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Guarantee</h3>
              <p className="text-gray-600">
                We stand behind our work with comprehensive warranties and exceptional customer service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Contact us today for a free consultation and estimate. Call (928) 427-8140.
          </p>
          <a href="tel:928-427-8140" className="inline-block bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-phone-line mr-2"></i>
            Get Your Free Quote
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
